<template>
  <div>
    <!-- Header -->
    <h2 class="text-center">사용자 관리</h2>
    <p class="text-center">
      총 <strong>{{ total }}</strong>개 계정
    </p>

    <div class="row justify-content-md-center">
      <div
        class="col-md-auto btn-group"
        role="group"
        style="padding-bottom: 20px"
      >
        <button
          type="button"
          class="btn btn-success"
          data-toggle="modal"
          data-target="#createUser"
        >
          계정 생성
        </button>
        <button type="button" class="btn btn-secondary" @click="updatePage">
          새로고침
        </button>
      </div>
    </div>

    <div class="row justify-content-md-center" style="padding-bottom: 10px">
      <div class="col-md-2 text-right">
        <span>표시 개수</span>
      </div>
      <div class="col-md-2">
        <select
          v-model="limit"
          class="form-control form-control-sm text-inline"
        >
          <option>50</option>
          <option>100</option>
          <option>500</option>
          <option>1000</option>
        </select>
      </div>
    </div>

    <!-- Search + Sort bar -->
    <div class="row justify-content-md-center" style="padding-bottom: 15px">
      <div class="col-md-4">
        <input
          v-model="searchQuery"
          class="form-control form-control-sm"
          placeholder="기관명 검색"
        />
      </div>
    </div>

    <!-- Role-based accordion -->
    <div class="accordion-wrapper">
      <b-card
        v-for="(group, key) in usersByRole"
        :key="key"
        no-body
        class="mb-2"
      >
        <b-card-header
          class="d-flex justify-content-between align-items-center"
          style="cursor: pointer;"
          @click="toggleSection(key)"
        >
          <span>
            <span class="badge" :class="group.badge">{{ group.label }}</span>
            <span class="ml-2">{{ group.activeUsers.length }}개 계정</span>
            <span v-if="group.inactiveUsers.length > 0" class="text-muted ml-1">(+{{ group.inactiveUsers.length }} 비활성)</span>
          </span>
          <i
            class="fa"
            :class="openSections[key] ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </b-card-header>
        <b-collapse :visible="openSections[key]">
          <div class="table-responsive-wrapper">
            <table class="table table-hover table-sm mb-0">
              <thead class="remove-top-border">
                <tr>
                  <th scope="col" style="cursor: pointer;" @click="toggleSort('id')">
                    ID
                    <i v-if="sortBy === 'id'" :class="sortAsc ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" style="font-size: 12px;"></i>
                    <i v-else class="fa fa-sort" style="font-size: 12px; color: #ccc;"></i>
                  </th>
                  <th scope="col" style="cursor: pointer;" @click="toggleSort('name')">
                    기관명
                    <i v-if="sortBy === 'name'" :class="sortAsc ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" style="font-size: 12px;"></i>
                    <i v-else class="fa fa-sort" style="font-size: 12px; color: #ccc;"></i>
                  </th>
                  <th v-if="key === 'demo' || key === 'unlimited'" scope="col">유통사</th>
                  <th scope="col">API타입</th>
                  <th scope="col">계정타입</th>
                  <th scope="col" style="cursor: pointer;" @click="toggleSort('expiration')">
                    {{ key === 'demo' ? '기간' : '만료일' }}
                    <i v-if="sortBy === 'expiration'" :class="sortAsc ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" style="font-size: 12px;"></i>
                    <i v-else class="fa fa-sort" style="font-size: 12px; color: #ccc;"></i>
                  </th>
                  <th scope="col">AI사용량</th>
                  <th class="text-center" scope="col">설정</th>
                  <th class="text-center" scope="col">삭제</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(user, index) in group.activeUsers">
                <tr :key="'main-' + index" :style="user.sub_account_count > 0 ? 'cursor: pointer;' : ''" @click="user.sub_account_count > 0 ? toggleSubAccounts(user) : null">
                  <td>
                    <span v-if="user.sub_account_count > 0" class="mr-1">
                      <i class="fa" :class="expandedUsers[user.username] ? 'fa-caret-down' : 'fa-caret-right'"></i>
                    </span>
                    {{ user.username }}
                    <span v-if="user.sub_account_count > 0" class="badge badge-light ml-1" title="부계정 수">+{{ user.sub_account_count }}</span>
                    <span v-if="user.is_distributor" class="badge badge-info ml-1">유통사</span>
                    <span v-if="key === 'bot' && user.role === 'admin'" class="badge badge-danger ml-1">Admin</span>
                    <span v-else-if="key === 'bot' && user.role === 'operator'" class="badge badge-warning ml-1">Operator</span>
                    <span v-else-if="key === 'bot'" class="badge badge-secondary ml-1">User</span>
                    <span v-else-if="user.is_bot" class="badge badge-dark ml-1">Bot</span>
                  </td>
                  <td>{{ user.name }}</td>
                  <!-- Distributor (demo/unlimited only) -->
                  <td v-if="key === 'demo' || key === 'unlimited'">
                    {{ getDistributorDisplay(user) }}
                  </td>
                  <!-- User Type -->
                  <td>
                    <span v-if="user.user_type === 'api'" class="badge badge-info">API</span>
                    <span v-else-if="user.user_type === 'both'" class="badge badge-primary">Both</span>
                    <span v-else class="badge badge-secondary">Web</span>
                  </td>
                  <!-- Account Type -->
                  <td>
                    <span v-if="user.account_type === 'demo'" class="badge badge-warning">Demo</span>
                    <span v-else-if="user.account_type === 'unlimited'" class="badge badge-success">Unlimited</span>
                    <span v-else-if="user.account_type === 'prepaid'" class="badge badge-primary">Prepaid</span>
                    <span v-else class="badge badge-secondary">Unknown</span>
                  </td>
                  <!-- Expiration Date / Period -->
                  <td>
                    <span v-if="user.permissions && user.permissions.has_expiration === false">-</span>
                    <span v-else-if="key === 'demo' && user.created_at && user.subscription_expiration">
                      {{ user.created_at.split('T')[0] }} ~ {{ new Date(user.subscription_expiration.$date).toISOString().split('T')[0] }}
                    </span>
                    <span v-else-if="user.subscription_expiration">
                      {{ new Date(user.subscription_expiration.$date).toISOString().split('T')[0] }}
                    </span>
                  </td>
                  <!-- AI Usage -->
                  <td>
                    <span v-if="user.account_type === 'prepaid'">
                      <span class="badge badge-primary">{{ user.prepaid_available != null ? user.prepaid_available : '…' }}</span> credit
                    </span>
                    <span v-else-if="user.user_type === 'api'">
                      <span class="badge badge-primary">API</span>
                    </span>
                    <span v-else-if="user.account_type === 'unlimited' || user.is_admin">
                      <span class="badge badge-success">Unlimited</span>
                      <br>
                      <small class="text-muted">Used: {{ user.ai_analysis_used || 0 }}</small>
                    </span>
                    <span v-else-if="user.ai_analysis_limit !== undefined">
                      {{ user.ai_analysis_used || 0 }}/{{ user.ai_analysis_limit }}
                      <small class="text-muted">({{ user.ai_analysis_limit - (user.ai_analysis_used || 0) }} left)</small>
                    </span>
                    <span v-else>N/A</span>
                  </td>
                  <!-- Settings button -->
                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click="openSettings(user)"
                      title="설정"
                    >
                      <i class="fa fa-cog" style="cursor: pointer;"></i> 설정
                    </button>
                  </td>
                  <!-- Delete icon -->
                  <td class="text-center">
                    <i
                      v-if="$store.getters['user/isAdmin']"
                      class="fa fa-trash-o delete-icon"
                      @click="quickDeleteUser(user)"
                      title="계정 삭제"
                    />
                  </td>
                </tr>
                <!-- Sub-account rows (accordion) -->
                <tr v-for="(sub, sIdx) in (expandedUsers[user.username] && subAccountsData[user.username] ? subAccountsData[user.username] : [])" :key="'sub-' + index + '-' + sIdx" style="background-color: #f8f9fa;">
                  <td style="padding-left: 2rem;">
                    <i class="fa fa-level-up fa-rotate-90 text-muted mr-1"></i>
                    {{ sub.username }}
                    <span class="badge badge-light ml-1">부계정</span>
                  </td>
                  <td>{{ sub.name }}</td>
                  <td v-if="key === 'demo' || key === 'unlimited'">
                    {{ getDistributorDisplay(sub) }}
                  </td>
                  <td>
                    <span v-if="sub.user_type === 'api'" class="badge badge-info">API</span>
                    <span v-else-if="sub.user_type === 'both'" class="badge badge-primary">Both</span>
                    <span v-else class="badge badge-secondary">Web</span>
                  </td>
                  <td>
                    <span v-if="sub.account_type === 'demo'" class="badge badge-warning">Demo</span>
                    <span v-else-if="sub.account_type === 'unlimited'" class="badge badge-success">Unlimited</span>
                    <span v-else-if="sub.account_type === 'prepaid'" class="badge badge-primary">Prepaid</span>
                  </td>
                  <td>
                    <span v-if="key === 'demo' && sub.created_at && sub.subscription_expiration && sub.subscription_expiration.$date">
                      {{ sub.created_at.split('T')[0] }} ~ {{ new Date(sub.subscription_expiration.$date).toISOString().split('T')[0] }}
                    </span>
                    <span v-else-if="sub.subscription_expiration && sub.subscription_expiration.$date">{{ new Date(sub.subscription_expiration.$date).toISOString().split('T')[0] }}</span>
                  </td>
                  <td>
                    <span v-if="sub.account_type === 'unlimited'"><span class="badge badge-success">Unlimited</span></span>
                    <span v-else-if="sub.ai_analysis_limit !== undefined">{{ sub.ai_analysis_used || 0 }}/{{ sub.ai_analysis_limit }}</span>
                  </td>
                  <td class="text-center">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click.stop="openSettings(sub)" title="설정">
                      <i class="fa fa-cog"></i>
                    </button>
                  </td>
                  <td class="text-center">
                    <i v-if="$store.getters['user/isAdmin']" class="fa fa-trash-o delete-icon" @click.stop="quickDeleteUser(sub)" title="계정 삭제" />
                  </td>
                </tr>
                </template>
                <tr v-if="group.activeUsers.length === 0 && group.inactiveUsers.length === 0">
                  <td :colspan="(key === 'demo' || key === 'unlimited') ? 9 : 8" class="text-center text-muted">해당 역할의 계정이 없습니다</td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- 비활성(만료/비활성화) 사용자 아코디언 -->
          <div v-if="group.inactiveUsers.length > 0" class="px-3 pb-2">
            <small
              class="text-muted d-block mt-2 mb-1"
              style="cursor: pointer;"
              @click="toggleInactiveSection(key)"
            >
              <i class="fa" :class="openInactiveSections[key] ? 'fa-chevron-up' : 'fa-chevron-down'" />
              만료/비활성 ({{ group.inactiveUsers.length }}개)
            </small>
            <b-collapse :visible="openInactiveSections[key] || false">
              <div class="table-responsive-wrapper">
                <table class="table table-hover table-sm mb-0" style="opacity: 0.7;">
                  <tbody>
                    <tr v-for="(user, idx) in group.inactiveUsers" :key="'inactive-' + idx">
                      <td>
                        {{ user.username }}
                        <span v-if="user.is_distributor" class="badge badge-info ml-1">유통사</span>
                        <span v-if="user.is_bot" class="badge badge-dark ml-1">Bot</span>
                      </td>
                      <td>{{ user.name }}</td>
                      <td v-if="key === 'demo' || key === 'unlimited'">
                        {{ getDistributorDisplay(user) }}
                      </td>
                      <td>
                        <span v-if="user.user_type === 'api'" class="badge badge-info">API</span>
                        <span v-else-if="user.user_type === 'both'" class="badge badge-primary">Both</span>
                        <span v-else class="badge badge-secondary">Web</span>
                      </td>
                      <td>
                        <span v-if="user.account_type === 'deactivated'" class="badge badge-dark">Deactivated</span>
                        <span v-else-if="user.account_type === 'demo'" class="badge badge-warning">Demo</span>
                        <span v-else class="badge badge-secondary">{{ user.account_type }}</span>
                      </td>
                      <td>
                        <span v-if="key === 'demo' && user.created_at && user.subscription_expiration" class="text-danger">
                          {{ user.created_at.split('T')[0] }} ~ {{ new Date(user.subscription_expiration.$date).toISOString().split('T')[0] }}
                        </span>
                        <span v-else-if="user.subscription_expiration" class="text-danger">
                          {{ new Date(user.subscription_expiration.$date).toISOString().split('T')[0] }}
                        </span>
                      </td>
                      <td>
                        <span v-if="user.user_type === 'api'"><span class="badge badge-primary">API</span></span>
                        <span v-else>{{ user.ai_analysis_used || 0 }}/{{ user.ai_analysis_limit || 0 }}</span>
                      </td>
                      <td class="text-center">
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click="openSettings(user)" title="설정">
                          <i class="fa fa-cog"></i> 설정
                        </button>
                      </td>
                      <td class="text-center">
                        <i v-if="$store.getters['user/isAdmin']" class="fa fa-trash-o delete-icon" @click="quickDeleteUser(user)" title="계정 삭제" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-collapse>
          </div>
        </b-collapse>
      </b-card>
    </div>

    <!-- Create User modal (jQuery) -->
    <div class="modal fade" tabindex="-1" role="dialog" id="createUser">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">계정 생성</h5>
            <button
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
              id="closeCreateUser"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <!-- 템플릿 선택 화면 -->
            <div v-if="!create.template">
              <p class="text-muted mb-3">생성할 계정 유형을 선택하세요</p>
              <div class="row">
                <div
                  v-for="tmpl in accountTemplates"
                  :key="tmpl.key"
                  class="col-6 mb-2"
                >
                  <div
                    class="card text-center p-3 h-100"
                    style="cursor: pointer; transition: box-shadow 0.2s;"
                    @click="selectTemplate(tmpl)"
                    @mouseenter="$event.currentTarget.style.boxShadow = '0 0 8px rgba(0,123,255,0.3)'"
                    @mouseleave="$event.currentTarget.style.boxShadow = 'none'"
                  >
                    <div><i :class="'fa ' + tmpl.icon" style="font-size: 2em;"></i></div>
                    <strong class="mt-2">{{ tmpl.label }}</strong>
                    <small class="text-muted">{{ tmpl.desc }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- 폼 화면 (템플릿 선택 후) -->
            <div v-else>
              <a href="#" @click.prevent="create.template = ''" class="text-muted mb-3 d-block">
                <i class="fa fa-arrow-left"></i> 유형 다시 선택
              </a>
              <span class="badge mb-3 d-inline-block" :class="selectedTemplate ? selectedTemplate.badgeClass : ''">
                {{ selectedTemplate ? selectedTemplate.label : '' }}
              </span>

              <!-- 고급 모드: 기존 폼 전체 -->
              <form v-if="create.template === 'advanced'">
                <div class="form-group" :class="{ 'was-validated': validUsername(create.username) }">
                  <label>ID</label>
                  <input v-model="create.username" :class="inputUsernameClasses(create.username)" class="form-control" placeholder="ID" required />
                  <div class="invalid-feedback">Invalid Username</div>
                </div>
                <div class="form-group" :class="{ 'was-validated': validPassword(create.password) }">
                  <label>비밀번호</label>
                  <input v-model="create.password" :class="inputPasswordClasses(create.password)" class="form-control" placeholder="비밀번호" type="password" required />
                  <div class="invalid-feedback">
                    <div v-if="!validPasswordLength(create.password)"> At least 8 characters. </div>
                    <div v-if="!validPasswordUppercase(create.password)"> At least one uppercase letter. </div>
                    <div v-if="!validPasswordLowercase(create.password)"> At least one lowercase letter. </div>
                    <div v-if="!validPasswordNumber(create.password)"> At least one number. </div>
                    <div v-if="!validPasswordSpecial(create.password)"> At least one special character. </div>
                  </div>
                </div>
                <div class="form-group" :class="{ 'was-validated': create.name.length !== 0 }">
                  <label>기관명</label>
                  <input v-model="create.name" class="form-control" placeholder="기관명" required />
                </div>
                <div class="form-group">
                  <label>권한 (Role)</label>
                  <select v-model="create.role" class="form-control">
                    <option value="user">일반 사용자</option>
                    <option value="operator">운영자 (Operator)</option>
                    <option v-if="$store.getters['user/isAdmin']" value="admin">관리자 (Admin)</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>플래그</label>
                  <div class="form-check">
                    <input v-model="create.is_distributor" type="checkbox" class="form-check-input" id="createIsDistributorAdv">
                    <label class="form-check-label" for="createIsDistributorAdv">유통사 (Distributor)</label>
                  </div>
                  <div class="form-check">
                    <input v-model="create.is_bot" type="checkbox" class="form-check-input" id="createIsBotAdv">
                    <label class="form-check-label" for="createIsBotAdv">봇 계정 (Bot)</label>
                  </div>
                </div>
                <div class="form-group">
                  <label>사용자 유형</label>
                  <select v-model="create.user_type" class="form-control">
                    <option value="web">웹 전용</option>
                    <option value="api">API 전용</option>
                    <option value="both">웹 + API</option>
                  </select>
                </div>
                <div class="form-group" v-if="create.role === 'user' && !create.is_bot">
                  <label>계정 유형</label>
                  <div class="btn-group btn-group-toggle d-flex" data-toggle="buttons">
                    <label class="btn btn-outline-secondary" :class="{ active: create.account_type === 'demo' }" @click="create.account_type = 'demo'">
                      <input type="radio" v-model="create.account_type" value="demo"> Demo (100건)
                    </label>
                    <label class="btn btn-outline-secondary" :class="{ active: create.account_type === 'unlimited' }" @click="create.account_type = 'unlimited'">
                      <input type="radio" v-model="create.account_type" value="unlimited"> Unlimited
                    </label>
                    <label class="btn btn-outline-secondary" :class="{ active: create.account_type === 'prepaid' }" @click="create.account_type = 'prepaid'">
                      <input type="radio" v-model="create.account_type" value="prepaid"> Prepaid (선불)
                    </label>
                  </div>
                  <small class="form-text text-muted">운영자/관리자/봇 계정은 자동으로 Unlimited 입니다. API 전용 + Prepaid 조합으로 외부 API 선불 계정을 만들 수 있습니다.</small>
                </div>
                <div class="form-group" v-if="create.is_distributor">
                  <small class="form-text text-info">
                    <i class="fa fa-info-circle"></i>
                    유통사 계정: 웹 유통사는 하위 기관 대시보드만, API 유통사는 inference 가능
                  </small>
                </div>
                <div class="form-group" v-show="!(create.is_distributor && create.user_type === 'web')">
                  <label>Dataset 옵션</label>
                  <div class="form-check">
                    <input v-model="create.dataset_options" type="checkbox" class="form-check-input" id="datasetScreeningAdv" value="screening" />
                    <label class="form-check-label" for="datasetScreeningAdv"><span class="badge badge-primary">검진</span> Dataset 생성</label>
                  </div>
                  <div class="form-check">
                    <input v-model="create.dataset_options" type="checkbox" class="form-check-input" id="datasetNonReimbursedAdv" value="non_reimbursed" />
                    <label class="form-check-label" for="datasetNonReimbursedAdv"><span class="badge badge-warning text-dark">비급여</span> Dataset 생성</label>
                  </div>
                </div>
                <div class="form-group" v-show="!create.is_distributor">
                  <label>공유할 기관 (선택사항)</label>
                  <div style="max-height: 200px; overflow-y: auto; border: 1px solid #ced4da; border-radius: 0.25rem; padding: 0.5rem;">
                    <div v-for="inst in groupedDatasets" :key="inst.institution_name" class="form-check" style="display: flex; align-items: center;">
                      <input type="checkbox" class="form-check-input" style="margin-top: 0;" :id="'instAdv-' + inst.institution_name" :checked="isInstitutionSelected(inst)" @change="toggleInstitution(inst)" />
                      <label class="form-check-label" :for="'instAdv-' + inst.institution_name" style="margin-right: 8px;">{{ inst.institution_name }}</label>
                      <span v-for="ds in inst.datasets" :key="ds.id" class="badge mr-1" :class="ds.exam_type === 'screening' ? 'badge-primary' : 'badge-warning'">{{ ds.exam_type === 'screening' ? '검진' : '비급여' }}</span>
                    </div>
                    <div v-if="groupedDatasets.length === 0" class="text-muted text-center">공유 가능한 Dataset이 없습니다</div>
                  </div>
                </div>
              </form>

              <!-- 템플릿 모드: 간소화된 폼 -->
              <form v-else>
                <div class="form-group" :class="{ 'was-validated': validUsername(create.username) }">
                  <label>ID</label>
                  <input v-model="create.username" :class="inputUsernameClasses(create.username)" class="form-control" placeholder="ID" required />
                  <div class="invalid-feedback">Invalid Username</div>
                </div>
                <div class="form-group" :class="{ 'was-validated': validPassword(create.password) }">
                  <label>비밀번호</label>
                  <input v-model="create.password" :class="inputPasswordClasses(create.password)" class="form-control" placeholder="비밀번호" type="password" required />
                  <div class="invalid-feedback">
                    <div v-if="!validPasswordLength(create.password)"> At least 8 characters. </div>
                    <div v-if="!validPasswordUppercase(create.password)"> At least one uppercase letter. </div>
                    <div v-if="!validPasswordLowercase(create.password)"> At least one lowercase letter. </div>
                    <div v-if="!validPasswordNumber(create.password)"> At least one number. </div>
                    <div v-if="!validPasswordSpecial(create.password)"> At least one special character. </div>
                  </div>
                </div>
                <div class="form-group" :class="{ 'was-validated': create.name.length !== 0 }">
                  <label>기관명</label>
                  <input v-model="create.name" class="form-control" placeholder="기관명" required />
                </div>
                <!-- 계정 유형 (demo/unlimited/prepaid) -->
                <div class="form-group" v-if="selectedTemplate && selectedTemplate.showAccountType">
                  <label>계정 유형</label>
                  <div class="btn-group btn-group-toggle d-flex" data-toggle="buttons">
                    <label class="btn btn-outline-secondary" :class="{ active: create.account_type === 'demo' }" @click="create.account_type = 'demo'">
                      <input type="radio" v-model="create.account_type" value="demo"> Demo (100건)
                    </label>
                    <label class="btn btn-outline-secondary" :class="{ active: create.account_type === 'unlimited' }" @click="create.account_type = 'unlimited'">
                      <input type="radio" v-model="create.account_type" value="unlimited"> Unlimited
                    </label>
                    <label class="btn btn-outline-secondary" :class="{ active: create.account_type === 'prepaid' }" @click="create.account_type = 'prepaid'">
                      <input type="radio" v-model="create.account_type" value="prepaid"> Prepaid (선불)
                    </label>
                  </div>
                </div>
                <!-- Dataset 옵션 (일반 설정) -->
                <div class="form-group" v-if="selectedTemplate && selectedTemplate.showAdvanced" v-show="!(create.is_distributor && create.user_type === 'web')">
                  <label>Dataset 옵션</label>
                  <div class="form-check">
                    <input v-model="create.dataset_options" type="checkbox" class="form-check-input" id="datasetScreeningTmpl" value="screening" />
                    <label class="form-check-label" for="datasetScreeningTmpl"><span class="badge badge-primary">검진</span> Dataset 생성</label>
                  </div>
                  <div class="form-check">
                    <input v-model="create.dataset_options" type="checkbox" class="form-check-input" id="datasetNonReimbursedTmpl" value="non_reimbursed" />
                    <label class="form-check-label" for="datasetNonReimbursedTmpl"><span class="badge badge-warning text-dark">비급여</span> Dataset 생성</label>
                  </div>
                  <small class="form-text text-muted">선택하지 않으면 Dataset을 생성하지 않습니다</small>
                </div>
                <!-- 부계정 개수 -->
                <div class="form-group" v-if="selectedTemplate && selectedTemplate.showSubAccounts">
                  <label>부계정 개수</label>
                  <input v-model.number="create.sub_account_count" type="number" class="form-control" min="0" max="20" placeholder="0 (부계정 없음)" />
                  <small v-if="create.sub_account_count > 0 && create.username" class="form-text text-muted">
                    부계정 ID: <span v-for="n in create.sub_account_count" :key="n">{{ create.username }}{{ n + 1 }}<span v-if="n < create.sub_account_count">, </span></span>
                  </small>
                </div>
                <!-- 고급 설정 (접힌 상태) — 공유 dataset만 남음 -->
                <div v-if="selectedTemplate && selectedTemplate.showSharedDatasets" class="mt-3">
                  <a href="#" @click.prevent="showAdvancedCreate = !showAdvancedCreate" class="text-muted">
                    <i class="fa" :class="showAdvancedCreate ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    고급 설정
                  </a>
                  <b-collapse :visible="showAdvancedCreate">
                    <div class="mt-2">
                      <div class="form-group">
                        <label>공유할 기관 (선택사항)</label>
                        <div style="max-height: 200px; overflow-y: auto; border: 1px solid #ced4da; border-radius: 0.25rem; padding: 0.5rem;">
                          <div v-for="inst in groupedDatasets" :key="inst.institution_name" class="form-check" style="display: flex; align-items: center;">
                            <input type="checkbox" class="form-check-input" style="margin-top: 0;" :id="'instTmpl-' + inst.institution_name" :checked="isInstitutionSelected(inst)" @change="toggleInstitution(inst)" />
                            <label class="form-check-label" :for="'instTmpl-' + inst.institution_name" style="margin-right: 8px;">{{ inst.institution_name }}</label>
                            <span v-for="ds in inst.datasets" :key="ds.id" class="badge mr-1" :class="ds.exam_type === 'screening' ? 'badge-primary' : 'badge-warning'">{{ ds.exam_type === 'screening' ? '검진' : '비급여' }}</span>
                          </div>
                          <div v-if="groupedDatasets.length === 0" class="text-muted text-center">공유 가능한 Dataset이 없습니다</div>
                        </div>
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer" v-if="create.template">
            <button type="submit" class="btn btn-primary" @click="createUser">
              생성
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Unified Settings modal (Bootstrap-Vue) -->
    <b-modal
      id="userSettingsModal"
      size="lg"
      :title="'설정: ' + (selectedUser ? selectedUser.username : '')"
      hide-footer
    >
      <b-tabs>
        <!-- Tab: 계정정보 -->
        <b-tab title="계정정보" active>
          <div class="pt-3">
            <div class="form-group">
              <label>ID</label>
              <input
                :value="selectedUser ? selectedUser.username : ''"
                class="form-control"
                readonly
              />
            </div>
            <div class="form-group">
              <label>기관명</label>
              <input
                v-model="settingsForm.name"
                class="form-control"
                placeholder="기관명"
              />
            </div>
            <div class="form-group">
              <label>비밀번호 변경</label>
              <input
                v-model="settingsForm.password"
                class="form-control"
                placeholder="새 비밀번호 (변경 시에만 입력)"
                type="password"
              />
            </div>
            <div class="mt-3 text-right">
              <b-button variant="primary" size="sm" @click="saveAccountInfo">저장</b-button>
            </div>
          </div>
        </b-tab>

        <!-- Tab: 권한/타입 -->
        <b-tab title="권한/타입">
          <div class="pt-3">
            <div class="form-group">
              <label>계정 타입</label>
              <select v-model="settingsForm.accountType" class="form-control">
                <option value="demo">Demo (제한)</option>
                <option value="unlimited">Unlimited (무제한)</option>
              </select>
              <small class="form-text text-muted">
                Demo: AI 분석 제한 / Unlimited: 무제한
              </small>
            </div>
            <div class="form-group">
              <label>API 타입</label>
              <select v-model="settingsForm.userType" class="form-control">
                <option value="web">Web (웹 전용)</option>
                <option value="api">API (API 전용)</option>
                <option value="both">Both (웹 + API)</option>
              </select>
              <small class="form-text text-muted">
                Web: 웹만 / API: External API만 / Both: 둘 다
              </small>
            </div>
            <div class="mt-3 text-right">
              <b-button variant="primary" size="sm" @click="saveAccountType">저장</b-button>
            </div>
          </div>
        </b-tab>

        <!-- Tab: 사용량 -->
        <b-tab title="사용량">
          <div class="pt-3">
            <div class="form-group">
              <label>계정 만료일</label>
              <input
                v-model="settingsForm.subscription"
                type="date"
                class="form-control"
              />
            </div>
            <div class="form-group" v-show="settingsForm.accountType === 'demo'">
              <label>AI 분석 한도</label>
              <input
                v-model.number="settingsForm.aiLimit"
                type="number"
                min="0"
                class="form-control"
                placeholder="AI 분석 한도"
                :disabled="selectedUser && selectedUser.user_type === 'api'"
              />
              <small v-if="selectedUser && selectedUser.user_type === 'api'" class="form-text text-info">
                <i class="fa fa-info-circle"></i> API Demo Quota로 관리됩니다
              </small>
              <small v-else class="form-text text-muted">누적 AI 분석 요청 한도</small>
            </div>
            <div class="form-group" v-if="selectedUser">
              <label>현재 사용량</label>
              <p class="form-control-plaintext">
                <span v-if="selectedUser.account_type === 'unlimited' || selectedUser.is_admin">
                  <span class="badge badge-success">Unlimited</span>
                  (Used: {{ selectedUser.ai_analysis_used || 0 }})
                </span>
                <span v-else>
                  {{ selectedUser.ai_analysis_used || 0 }} / {{ selectedUser.ai_analysis_limit || 0 }}
                </span>
              </p>
            </div>
            <div class="mt-3 text-right">
              <b-button variant="primary" size="sm" @click="saveUsage">저장</b-button>
            </div>
          </div>
        </b-tab>

        <!-- Tab: Dataset -->
        <b-tab title="Dataset">
          <div class="pt-3">
            <!-- 현재 Dataset 목록 -->
            <label>보유 Dataset</label>
            <div v-if="userDatasets.loading" class="text-center text-muted py-3">
              <i class="fa fa-spinner fa-spin"></i> 로딩 중...
            </div>
            <ul v-else class="list-group mb-3">
              <li
                v-for="ds in userDatasets.datasets"
                :key="ds.id"
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                <span>
                  <span
                    class="badge mr-2"
                    :class="ds.exam_type === 'screening' ? 'badge-primary' : 'badge-warning'"
                  >
                    {{ ds.exam_type === 'screening' ? '검진' : '비급여' }}
                  </span>
                  {{ ds.name }}
                  <small class="text-muted ml-2">({{ ds.image_count }} images)</small>
                </span>
                <button
                  v-if="$store.getters['user/isAdmin']"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="deleteUserDataset(ds)"
                  title="삭제"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </li>
              <li v-if="userDatasets.datasets.length === 0" class="list-group-item text-muted text-center">
                보유한 Dataset이 없습니다
              </li>
            </ul>

            <!-- Dataset 생성 버튼 -->
            <label>Dataset 추가</label>
            <div class="d-flex">
              <button
                type="button"
                class="btn btn-primary mr-2"
                @click="createUserDataset('screening')"
                :disabled="hasDatasetType('screening')"
              >
                <i class="fa fa-plus mr-1"></i>
                <span class="badge badge-light">검진</span> Dataset
              </button>
              <button
                type="button"
                class="btn btn-warning"
                @click="createUserDataset('non_reimbursed')"
                :disabled="hasDatasetType('non_reimbursed')"
              >
                <i class="fa fa-plus mr-1"></i>
                <span class="badge badge-light">비급여</span> Dataset
              </button>
            </div>
            <small class="form-text text-muted mt-2">
              이미 존재하는 타입의 Dataset은 생성할 수 없습니다
            </small>
          </div>
        </b-tab>

        <!-- Tab: 정보 -->
        <b-tab title="정보">
          <div class="pt-3">
            <div class="form-group">
              <label>유통사 (Distributor)</label>
              <div class="d-flex align-items-center mb-2">
                <select
                  v-model="settingsForm.selectedDistributors"
                  class="form-control"
                  multiple
                  style="height: 80px; flex: 1;"
                >
                  <option v-for="d in distributorList" :key="d.id" :value="d.name">{{ d.name }}</option>
                </select>
              </div>
              <div v-if="settingsForm.selectedDistributors.length > 0" class="selected-tags mb-2">
                <span
                  v-for="name in settingsForm.selectedDistributors"
                  :key="name"
                  class="badge badge-info mr-1 mb-1"
                  style="font-size: 0.9em; padding: 5px 8px;"
                >
                  {{ name }}
                  <i
                    class="fa fa-times ml-1"
                    style="cursor: pointer;"
                    @click="removeSettingsDistributor(name)"
                    title="선택 해제"
                  ></i>
                </span>
              </div>
              <small class="form-text text-muted">Ctrl+클릭으로 다중 선택. 비워두면 모든 유통사 표시</small>
            </div>
            <div class="form-group">
              <label>기관 HQ</label>
              <div class="d-flex align-items-center mb-2">
                <select
                  v-model="settingsForm.selectedHqs"
                  class="form-control"
                  multiple
                  style="height: 80px; flex: 1;"
                >
                  <option v-for="h in hqList" :key="h.id" :value="h.name">{{ h.name }}</option>
                </select>
                <button
                  type="button"
                  class="btn btn-outline-secondary ml-2"
                  data-toggle="modal"
                  data-target="#manageHqs"
                  @click="loadHqs"
                  title="HQ 관리"
                >
                  <i class="fa fa-cog"></i>
                </button>
              </div>
              <div v-if="settingsForm.selectedHqs.length > 0" class="selected-tags mb-2">
                <span
                  v-for="name in settingsForm.selectedHqs"
                  :key="name"
                  class="badge badge-success mr-1 mb-1"
                  style="font-size: 0.9em; padding: 5px 8px;"
                >
                  {{ name }}
                  <i
                    class="fa fa-times ml-1"
                    style="cursor: pointer;"
                    @click="removeSettingsHq(name)"
                    title="선택 해제"
                  ></i>
                </span>
              </div>
              <small class="form-text text-muted">Ctrl+클릭으로 다중 선택. 비워두면 모든 HQ 표시</small>
            </div>
            <div class="form-group">
              <label>기관명 (Institution)</label>
              <input
                v-model="settingsForm.institutionInput"
                class="form-control"
                placeholder="기관명을 입력하세요"
              />
              <small class="form-text text-muted">이 값은 사용자 기관명(name)과 동기화됩니다</small>
            </div>
            <div class="mt-3 text-right">
              <b-button variant="primary" size="sm" @click="saveOrgInfo">저장</b-button>
            </div>
          </div>
        </b-tab>
      </b-tabs>

    </b-modal>

    <!-- HQ Management modal (jQuery) -->
    <div class="modal fade" tabindex="-1" role="dialog" id="manageHqs" style="z-index: 1060;">
      <div class="modal-dialog modal-sm" role="document">
        <div class="modal-content" style="background-color: #f8f9fa; border: 2px solid #28a745;">
          <div class="modal-header" style="background-color: #28a745; color: white;">
            <h5 class="modal-title"><i class="fa fa-cog mr-2"></i>HQ 관리</h5>
            <button
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
              id="closeManageHqs"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>신규 HQ 추가</label>
              <div class="input-group">
                <input
                  v-model="newHqName"
                  class="form-control"
                  placeholder="HQ명 입력"
                  @keyup.enter="addHq"
                />
                <div class="input-group-append">
                  <button
                    type="button"
                    class="btn btn-success"
                    @click="addHq"
                    :disabled="!newHqName.trim()"
                  >
                    추가
                  </button>
                </div>
              </div>
            </div>
            <hr>
            <label>등록된 HQ 목록</label>
            <ul class="list-group">
              <li
                v-for="h in hqList"
                :key="h.id"
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                {{ h.name }}
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="deleteHq(h)"
                  title="삭제"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </li>
              <li v-if="hqList.length === 0" class="list-group-item text-muted text-center">
                등록된 HQ가 없습니다
              </li>
            </ul>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Admin from "@/models/admin";
import toastrs from "@/mixins/toastrs";
import { mapMutations } from "vuex";
import axios from "@/libs/axios";

export default {
  name: "UserManagement",
  mixins: [toastrs],
  data() {
    return {
      users: [],
      limit: 50,
      total: 0,
      searchQuery: "",
      sortBy: "name",
      sortAsc: true,
      openSections: {
        admin: false,
        operator: false,
        bot: false,
        distributor: true,
        demo: true,
        unlimited: true
      },
      openInactiveSections: {},
      selectedUser: null,
      settingsForm: {
        name: "",
        password: "",
        accountType: "demo",
        userType: "web",
        subscription: "",
        aiLimit: 100,
        selectedDistributors: [],
        selectedHqs: [],
        institutionInput: ""
      },
      create: {
        template: "",
        name: "",
        username: "",
        password: "",
        role: "user",
        user_type: "web",
        is_distributor: false,
        is_bot: false,
        account_type: "demo",
        dataset_options: ["screening", "non_reimbursed"],
        shared_dataset_ids: [],
        sub_account_count: 0
      },
      showAdvancedCreate: false,
      distributorList: [],
      hqList: [],
      newHqName: "",
      groupedDatasets: [],
      availableDatasets: [],
      userDatasets: {
        username: "",
        datasets: [],
        loading: false
      },
      expandedUsers: {},
      subAccountsData: {}
    };
  },
  computed: {
    accountTemplates: function() {
      var templates = [
        { key: 'web_customer', label: '웹 고객 (병원)', icon: 'fa-hospital-o',
          desc: '일반 병원 계정', badgeClass: 'badge-primary',
          preset: { role: 'user', user_type: 'web', is_distributor: false, is_bot: false,
                    account_type: 'demo', dataset_options: ['screening', 'non_reimbursed'] },
          showAdvanced: true, showSharedDatasets: true, showAccountType: true, showSubAccounts: true },
        { key: 'distributor_web', label: '유통사 (웹)', icon: 'fa-building',
          desc: '웹 고객 모객 유통사', badgeClass: 'badge-info',
          preset: { role: 'user', user_type: 'web', is_distributor: true, is_bot: false,
                    dataset_options: [] },
          showAdvanced: false, showSharedDatasets: false },
        { key: 'distributor_api', label: '유통사 (API)', icon: 'fa-plug',
          desc: 'API 취합·재판매 유통사', badgeClass: 'badge-warning',
          preset: { role: 'user', user_type: 'api', is_distributor: true, is_bot: false,
                    dataset_options: ['screening', 'non_reimbursed'] },
          showAdvanced: true, showSharedDatasets: false },
        { key: 'api_customer', label: 'API 고객 (Demo)', icon: 'fa-code',
          desc: 'API 연동 데모 고객', badgeClass: 'badge-secondary',
          preset: { role: 'user', user_type: 'api', is_distributor: false, is_bot: false,
                    account_type: 'demo', dataset_options: ['screening', 'non_reimbursed'] },
          showAdvanced: true, showSharedDatasets: false, showAccountType: true, showSubAccounts: true },
        { key: 'api_prepaid', label: 'API 고객 (선불)', icon: 'fa-credit-card',
          desc: 'API 연동 선불(prepaid) 고객', badgeClass: 'badge-primary',
          preset: { role: 'user', user_type: 'api', is_distributor: false, is_bot: false,
                    account_type: 'prepaid', dataset_options: ['screening', 'non_reimbursed'] },
          showAdvanced: true, showSharedDatasets: false, showAccountType: true, showSubAccounts: true },
        { key: 'operator', label: '운영자', icon: 'fa-user-circle',
          desc: '사내 운영 담당자', badgeClass: 'badge-success',
          preset: { role: 'operator', user_type: 'both', is_distributor: false, is_bot: false,
                    dataset_options: [] },
          showAdvanced: false, showSharedDatasets: false }
      ];
      if (this.$store.getters['user/isAdmin']) {
        templates.push(
          { key: 'admin', label: '관리자', icon: 'fa-shield',
            desc: '시스템 관리자', badgeClass: 'badge-danger',
            preset: { role: 'admin', user_type: 'both', is_distributor: false, is_bot: false,
                      dataset_options: [] },
            showAdvanced: false, showSharedDatasets: false },
          { key: 'advanced', label: '고급 (직접 설정)', icon: 'fa-cogs',
            desc: '모든 옵션 직접 선택', badgeClass: 'badge-dark',
            preset: null, showAdvanced: true, showSharedDatasets: true, showAccountType: true, showSubAccounts: true }
        );
      }
      return templates;
    },
    selectedTemplate: function() {
      if (!this.create.template) return null;
      var self = this;
      var found = this.accountTemplates.filter(function(t) {
        return t.key === self.create.template;
      });
      return found.length > 0 ? found[0] : null;
    },
    filteredUsers: function() {
      var self = this;
      var users = self.users;
      if (self.searchQuery) {
        var query = self.searchQuery.toLowerCase();
        users = users.filter(function(u) {
          return (u.name || '').toLowerCase().indexOf(query) !== -1;
        });
      }
      if (self.sortBy === 'id') {
        users = users.slice().sort(function(a, b) {
          var result = (a.username || '').localeCompare(b.username || '');
          return self.sortAsc ? result : -result;
        });
      } else if (self.sortBy === 'expiration') {
        users = users.slice().sort(function(a, b) {
          var aDate = (a.subscription_expiration && a.subscription_expiration.$date) || 0;
          var bDate = (b.subscription_expiration && b.subscription_expiration.$date) || 0;
          return self.sortAsc ? (aDate - bDate) : (bDate - aDate);
        });
      } else {
        users = users.slice().sort(function(a, b) {
          var result = (a.name || '').localeCompare(b.name || '');
          return self.sortAsc ? result : -result;
        });
      }
      return users;
    },
    usersByRole: function() {
      var groups = {
        admin: { label: 'Admin', badge: 'badge-danger', users: [], activeUsers: [], inactiveUsers: [] },
        operator: { label: 'Operator', badge: 'badge-warning', users: [], activeUsers: [], inactiveUsers: [] },
        bot: { label: 'Bot', badge: 'badge-dark', users: [], activeUsers: [], inactiveUsers: [] },
        distributor: { label: 'Distributor', badge: 'badge-info', users: [], activeUsers: [], inactiveUsers: [] },
        demo: { label: 'Demo', badge: 'badge-secondary', users: [], activeUsers: [], inactiveUsers: [] },
        unlimited: { label: 'Unlimited', badge: 'badge-success', users: [], activeUsers: [], inactiveUsers: [] },
        prepaid: { label: 'Prepaid', badge: 'badge-primary', users: [], activeUsers: [], inactiveUsers: [] }
      };
      for (var i = 0; i < this.filteredUsers.length; i++) {
        var user = this.filteredUsers[i];
        var role = user.role || 'user';
        if (user.is_bot) groups.bot.users.push(user);
        else if (role === 'admin') groups.admin.users.push(user);
        else if (role === 'operator') groups.operator.users.push(user);
        else if (user.is_distributor) groups.distributor.users.push(user);
        else if (user.account_type === 'unlimited') groups.unlimited.users.push(user);
        else if (user.account_type === 'prepaid') groups.prepaid.users.push(user);
        else groups.demo.users.push(user);
      }
      // active/inactive 분리 (UTC epoch 기반 비교)
      var nowEpoch = Date.now();
      for (var key in groups) {
        var g = groups[key];
        for (var j = 0; j < g.users.length; j++) {
          var u = g.users[j];
          var hasExpiration = !(u.permissions && u.permissions.has_expiration === false);
          var expDate = u.subscription_expiration && u.subscription_expiration.$date;
          var isExpired = hasExpiration && expDate && expDate < nowEpoch;
          var isDeactivated = u.account_type === 'deactivated';
          if (isExpired || isDeactivated) {
            g.inactiveUsers.push(u);
          } else {
            g.activeUsers.push(u);
          }
        }
      }
      return groups;
    }
  },
  methods: {
    ...mapMutations(["addProcess", "removeProcess"]),
    getDistributorDisplay: function(user) {
      var dist = user.organization_info && user.organization_info.distributor;
      if (!dist) return '-';
      if (typeof dist === 'string') return dist || '-';
      if (Array.isArray(dist) && dist.length > 0) return dist.join(', ');
      return '-';
    },
    toggleSection: function(key) {
      this.$set(this.openSections, key, !this.openSections[key]);
    },
    toggleInactiveSection: function(key) {
      this.$set(this.openInactiveSections, key, !this.openInactiveSections[key]);
    },
    selectTemplate: function(template) {
      this.create.template = template.key;
      this.showAdvancedCreate = false;
      if (template.preset) {
        this.create.role = template.preset.role;
        this.create.user_type = template.preset.user_type;
        this.create.is_distributor = template.preset.is_distributor;
        this.create.is_bot = template.preset.is_bot;
        this.create.account_type = template.preset.account_type || 'demo';
        this.create.dataset_options = template.preset.dataset_options.slice();
        this.create.shared_dataset_ids = [];
        this.create.sub_account_count = 0;
      }
    },
    resetCreateForm: function() {
      this.create = {
        template: '',
        name: '',
        username: '',
        password: '',
        role: 'user',
        user_type: 'web',
        is_distributor: false,
        is_bot: false,
        account_type: 'demo',
        dataset_options: ['screening', 'non_reimbursed'],
        shared_dataset_ids: [],
        sub_account_count: 0
      };
      this.showAdvancedCreate = false;
    },
    toggleSort: function(field) {
      if (this.sortBy === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortBy = field;
        this.sortAsc = true;
      }
    },
    toggleSubAccounts: function(user) {
      var self = this;
      var username = user.username;
      var isExpanded = self.expandedUsers[username] || false;
      self.$set(self.expandedUsers, username, !isExpanded);
      // Lazy-load sub-accounts on first expand
      if (!isExpanded && !self.subAccountsData[username]) {
        axios.get('/api/admin/user/' + username + '/sub-accounts')
          .then(function(response) {
            if (response.data.success) {
              self.$set(self.subAccountsData, username, response.data.sub_accounts);
            }
          })
          .catch(function(error) {
            var msg = (error.response && error.response.data && error.response.data.message) || 'Failed to load sub-accounts';
            self.axiosReqestError("Sub-accounts", msg);
          });
      }
    },
    quickDeleteUser: function(user) {
      var self = this;
      var yes = confirm(user.username + ' 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.');
      if (!yes) return;
      Admin.deleteUser(user.username)
        .then(function() {
          // Clear sub-account cache if this was a sub-account
          if (user.parent_username && self.subAccountsData[user.parent_username]) {
            self.$set(self.subAccountsData, user.parent_username, null);
          }
          self.updatePage();
          self.$toastr.success('계정이 삭제되었습니다', '계정 삭제');
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || 'Delete failed';
          self.axiosReqestError("Delete User", msg);
        });
    },
    removeSettingsDistributor: function(name) {
      var idx = this.settingsForm.selectedDistributors.indexOf(name);
      if (idx > -1) {
        this.settingsForm.selectedDistributors.splice(idx, 1);
      }
    },
    removeSettingsHq: function(name) {
      var idx = this.settingsForm.selectedHqs.indexOf(name);
      if (idx > -1) {
        this.settingsForm.selectedHqs.splice(idx, 1);
      }
    },
    openSettings: function(user) {
      var self = this;
      self.selectedUser = user;
      self.settingsForm.name = user.name || '';
      self.settingsForm.password = '';
      self.settingsForm.accountType = user.account_type || 'demo';
      self.settingsForm.userType = user.user_type || 'web';
      self.settingsForm.subscription = user.subscription_expiration
        ? new Date(user.subscription_expiration.$date).toISOString().split('T')[0]
        : '2999-01-01';
      self.settingsForm.aiLimit = user.ai_analysis_limit || 100;
      self.settingsForm.selectedDistributors = [];
      self.settingsForm.selectedHqs = [];
      self.settingsForm.institutionInput = '';
      self.loadUserDatasets(user);
      self.loadDistributors();
      self.loadHqs();
      Admin.getOrgInfo(user.username)
        .then(function(response) {
          if (response.data.success) {
            var filters = response.data.filters;
            var dist = filters.distributor || [];
            if (typeof dist === 'string') {
              dist = dist ? [dist] : [];
            }
            self.settingsForm.selectedDistributors = dist;
            var hqs = filters.hq || [];
            if (typeof hqs === 'string') {
              hqs = hqs ? [hqs] : [];
            }
            self.settingsForm.selectedHqs = hqs;
            self.settingsForm.institutionInput = (filters.institution || []).join(', ');
          }
        })
        .catch(function(error) {
          console.error('Failed to load org info:', error);
          self.settingsForm.selectedDistributors = [];
          self.settingsForm.selectedHqs = [];
          self.settingsForm.institutionInput = user.name || '';
        });
      self.$bvModal.show('userSettingsModal');
    },
    saveAccountInfo: function() {
      var self = this;
      var u = self.selectedUser;
      if (!u) return;
      Admin.updateUser(u.username, self.settingsForm.name, self.settingsForm.password)
        .then(function() {
          self.updatePage();
          self.$toastr.success('계정정보가 저장되었습니다', '저장');
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || error.message;
          self.axiosReqestError('저장 실패', msg);
        });
    },
    saveAccountType: function() {
      var self = this;
      var u = self.selectedUser;
      if (!u) return;
      var promises = [];
      if (self.settingsForm.accountType !== u.account_type) {
        promises.push(Admin.updateUserAccountType(u.username, self.settingsForm.accountType));
      }
      if (self.settingsForm.userType !== u.user_type) {
        promises.push(Admin.updateUserType(u.username, self.settingsForm.userType));
      }
      if (promises.length === 0) {
        self.$toastr.info('변경된 내용이 없습니다', '알림');
        return;
      }
      Promise.all(promises)
        .then(function() {
          self.updatePage();
          self.$toastr.success('권한/타입이 저장되었습니다', '저장');
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || error.message;
          self.axiosReqestError('저장 실패', msg);
        });
    },
    saveUsage: function() {
      var self = this;
      var u = self.selectedUser;
      if (!u) return;
      var promises = [];
      var currentSub = u.subscription_expiration
        ? new Date(u.subscription_expiration.$date).toISOString().split('T')[0]
        : '';
      if (self.settingsForm.subscription !== currentSub) {
        promises.push(Admin.updateUserSubscription(u.username, self.settingsForm.subscription));
      }
      if (self.settingsForm.aiLimit !== u.ai_analysis_limit) {
        promises.push(Admin.updateUserAiLimit(u.username, self.settingsForm.aiLimit));
      }
      if (promises.length === 0) {
        self.$toastr.info('변경된 내용이 없습니다', '알림');
        return;
      }
      Promise.all(promises)
        .then(function() {
          self.updatePage();
          self.$toastr.success('사용량 설정이 저장되었습니다', '저장');
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || error.message;
          self.axiosReqestError('저장 실패', msg);
        });
    },
    saveOrgInfo: function() {
      var self = this;
      var u = self.selectedUser;
      if (!u) return;
      var parseInput = function(input) {
        if (!input || input.trim() === '') return [];
        return input.split(',').map(function(s) { return s.trim(); }).filter(function(s) { return s.length > 0; });
      };
      var filters = {
        distributor: self.settingsForm.selectedDistributors,
        hq: self.settingsForm.selectedHqs,
        institution: parseInput(self.settingsForm.institutionInput)
      };
      Admin.updateOrgInfo(u.username, filters)
        .then(function() {
          self.updatePage();
          self.$toastr.success('계정 정보가 저장되었습니다', '저장');
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || error.message;
          self.axiosReqestError('저장 실패', msg);
        });
    },
    confirmDeleteUser: function() {
      var self = this;
      if (!self.selectedUser) return;
      var yes = confirm(self.selectedUser.username + ' 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.');
      if (!yes) return;
      Admin.deleteUser(self.selectedUser.username)
        .then(function() {
          self.$bvModal.hide('userSettingsModal');
          self.updatePage();
          self.$toastr.success('계정이 삭제되었습니다', '계정 삭제');
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || 'Delete failed';
          self.axiosReqestError("Delete User", msg);
        });
    },
    updatePage: function() {
      var self = this;
      var process = "Loading users";
      self.addProcess(process);

      Admin.getUsers(self.limit)
        .then(function(response) {
          self.users = response.data.users;
          self.total = response.data.total;
        })
        .finally(function() {
          self.removeProcess(process);
        });
    },
    createUser: function(event) {
      var self = this;
      event.preventDefault();
      var payload = Object.assign({}, self.create);
      delete payload.template;
      Admin.createUser(payload)
        .then(function() {
          self.resetCreateForm();
          var close = document.getElementById("closeCreateUser");
          close.click();
          self.updatePage();
          self.loadAvailableDatasets();
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || 'Create failed';
          self.axiosReqestError("Create User", msg);
        });
    },
    loadAvailableDatasets: function() {
      var self = this;
      axios.get('/api/dataset/')
        .then(function(response) {
          self.availableDatasets = response.data || [];
        })
        .catch(function(error) {
          console.error('Failed to load datasets:', error);
        });
    },
    loadGroupedDatasets: function() {
      var self = this;
      Admin.getGroupedDatasets()
        .then(function(response) {
          if (response.data.success) {
            self.groupedDatasets = response.data.institutions || [];
          }
        })
        .catch(function(error) {
          console.error('Failed to load grouped datasets:', error);
        });
    },
    isInstitutionSelected: function(inst) {
      if (!inst.datasets || inst.datasets.length === 0) return false;
      var selectedIds = this.create.shared_dataset_ids;
      for (var i = 0; i < inst.datasets.length; i++) {
        if (selectedIds.indexOf(inst.datasets[i].id) === -1) {
          return false;
        }
      }
      return true;
    },
    toggleInstitution: function(inst) {
      var isCurrentlySelected = this.isInstitutionSelected(inst);
      var datasetIds = inst.datasets.map(function(ds) { return ds.id; });

      if (isCurrentlySelected) {
        this.create.shared_dataset_ids = this.create.shared_dataset_ids.filter(function(id) {
          return datasetIds.indexOf(id) === -1;
        });
      } else {
        for (var i = 0; i < datasetIds.length; i++) {
          if (this.create.shared_dataset_ids.indexOf(datasetIds[i]) === -1) {
            this.create.shared_dataset_ids.push(datasetIds[i]);
          }
        }
      }
    },
    loadUserDatasets: function(user) {
      var self = this;
      self.userDatasets.username = user.username;
      self.userDatasets.datasets = [];
      self.userDatasets.loading = true;

      Admin.getUserDatasets(user.username)
        .then(function(response) {
          if (response.data.success) {
            self.userDatasets.datasets = response.data.datasets || [];
          }
          self.userDatasets.loading = false;
        })
        .catch(function(error) {
          console.error('Failed to load user datasets:', error);
          self.userDatasets.loading = false;
        });
    },
    hasDatasetType: function(examType) {
      for (var i = 0; i < this.userDatasets.datasets.length; i++) {
        if (this.userDatasets.datasets[i].exam_type === examType) {
          return true;
        }
      }
      return false;
    },
    createUserDataset: function(examType) {
      var self = this;
      var username = self.userDatasets.username;
      Admin.createUserDataset(username, examType)
        .then(function(response) {
          if (response.data.success) {
            var typeLabel = examType === 'screening' ? '검진' : '비급여';
            self.$toastr.success(typeLabel + ' Dataset이 생성되었습니다.', 'Dataset 생성');
            self.loadUserDatasets({ username: username });
            self.loadGroupedDatasets();
            self.loadAvailableDatasets();
          }
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || 'Dataset 생성 실패';
          self.$toastr.error(msg, 'Dataset 생성 실패');
        });
    },
    deleteUserDataset: function(dataset) {
      var self = this;
      var username = self.userDatasets.username;
      var confirmMsg = dataset.name + '을(를) 삭제하시겠습니까?';
      if (dataset.image_count > 0) {
        confirmMsg += '\n\n' + '이 Dataset에는 ' + dataset.image_count + '개의 이미지가 있습니다.';
      }

      if (!confirm(confirmMsg)) return;

      Admin.deleteUserDataset(username, dataset.id)
        .then(function(response) {
          if (response.data.success) {
            self.$toastr.success(response.data.message, 'Dataset 삭제');
            self.loadUserDatasets({ username: username });
            self.loadGroupedDatasets();
            self.loadAvailableDatasets();
          }
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || 'Dataset 삭제 실패';
          self.$toastr.error(msg, 'Dataset 삭제 실패');
        });
    },
    validDate: function(date) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
      var parts = date.split("-");
      var day = parseInt(parts[2], 10);
      var month = parseInt(parts[1], 10);
      var year = parseInt(parts[0], 10);
      if (year < 1000 || year > 3000 || month == 0 || month > 12) return false;
      var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
        monthLength[1] = 29;
      return day > 0 && day <= monthLength[month - 1];
    },
    validPassword: function(password) {
      if (password.length < 8) return false;
      if (!/[a-z]/.test(password)) return false;
      if (!/[A-Z]/.test(password)) return false;
      if (!/[0-9]/.test(password)) return false;
      if (!/[^a-zA-Z0-9&\\;<>]/.test(password)) return false;
      return true;
    },
    validPasswordLength: function(password) {
      if (password.length < 8) return false;
      return true;
    },
    validPasswordLowercase: function(password) {
      if (!/[a-z]/.test(password)) return false;
      return true;
    },
    validPasswordUppercase: function(password) {
      if (!/[A-Z]/.test(password)) return false;
      return true;
    },
    validPasswordNumber: function(password) {
      if (!/[0-9]/.test(password)) return false;
      return true;
    },
    validPasswordSpecial: function(password) {
      if (!/[^a-zA-Z0-9&\\;<>]/.test(password)) return false;
      return true;
    },
    validUsername: function(username) {
      if (username.length < 4) return false;
      return /^[0-9a-zA-Z_.-]+$/.test(username);
    },
    inputUsernameClasses: function(username) {
      var isValid = this.validUsername(username);
      return {
        "is-invalid": !isValid && username.length != 0,
        "is-valid": isValid
      };
    },
    inputPasswordClasses: function(password) {
      var isValid = this.validPassword(password);
      return {
        "is-invalid": !isValid && password.length != 0,
        "is-valid": isValid
      };
    },
    inputDateClasses: function(date) {
      var isValid = this.validDate(date);
      return {
        "is-invalid": !isValid && date.length != 0,
        "is-valid": isValid
      };
    },
    loadDistributors: function() {
      var self = this;
      Admin.getDistributors()
        .then(function(response) {
          if (response.data.success) {
            self.distributorList = response.data.distributors;
          }
        })
        .catch(function(error) {
          console.error('Failed to load distributors:', error);
        });
    },
    loadHqs: function() {
      var self = this;
      Admin.getHqs()
        .then(function(response) {
          if (response.data.success) {
            self.hqList = response.data.hqs;
          }
        })
        .catch(function(error) {
          console.error('Failed to load HQs:', error);
        });
    },
    addHq: function() {
      var self = this;
      var name = self.newHqName.trim();
      if (!name) return;

      Admin.createHq(name)
        .then(function(response) {
          if (response.data.success) {
            self.$toastr.info('HQ가 추가되었습니다', 'HQ 추가');
            self.newHqName = '';
            self.loadHqs();
          }
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || '추가 실패';
          self.axiosReqestError('HQ 추가', msg);
        });
    },
    deleteHq: function(hq) {
      var self = this;
      var yes = confirm('HQ "' + hq.name + '"을(를) 삭제하시겠습니까?');
      if (!yes) return;

      Admin.deleteHq(hq.id)
        .then(function(response) {
          if (response.data.success) {
            self.$toastr.info('HQ가 삭제되었습니다', 'HQ 삭제');
            self.loadHqs();
          }
        })
        .catch(function(error) {
          var msg = (error.response && error.response.data && error.response.data.message) || '삭제 실패';
          self.axiosReqestError('HQ 삭제', msg);
        });
    }
  },
  watch: {
    limit: "updatePage"
  },
  created() {
    this.updatePage();
    this.loadAvailableDatasets();
    this.loadGroupedDatasets();
  },
  mounted() {
    var self = this;
    var createUserEl = document.getElementById('createUser');
    if (createUserEl) {
      createUserEl.addEventListener('hidden.bs.modal', function() {
        self.resetCreateForm();
      });
    }
  }
};
</script>

<style scoped>
.remove-top-border {
  border: none !important;
}

/* 테이블 헤더 줄바꿈 방지 */
.table th {
  white-space: nowrap;
}

/* 테이블 셀 날짜 등 줄바꿈 방지 */
.table td {
  white-space: nowrap;
}

/* 테이블 가로 스크롤 지원 */
.table-responsive-wrapper {
  overflow-x: auto;
  width: 100%;
}

.fa {
  margin: 0;
  padding: 2px;
  font-size: 16px;
  cursor: pointer;
}

.delete-icon:hover {
  color: red;
}

.fa-cog:hover {
  color: orange;
}

.accordion-wrapper .card {
  border-radius: 0.25rem;
}

.accordion-wrapper .card-header {
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
}

.accordion-wrapper .card-header:hover {
  background-color: #e9ecef;
}
</style>
